import {
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
} from '@mui/material';
import FormActionButtons from 'components/ui/form/FormActionButtons';
import FormSelectField from 'components/ui/form/FormSelectField';
import FormFileUploadField from 'components/ui/form/FormFileUploadField';
import FormCurrencyField from 'components/ui/form/FormCurrencyField';
import FormTextField from 'components/ui/form/FormTextField';
import { useFormik } from 'formik';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useFormSubmit from 'hooks/useFormSubmit';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import * as yup from 'yup';

const validationSchema = yup.object({
  name: yup
    .string()
    .min(4, { label: 'products.validation.nameMin', value: 4 })
    .required('products.validation.name'),
  unit_id: yup.number().required('products.validation.role'),
  category_id: yup.number().required('products.validation.role'),
});

const StaffAddEditModal = (props) => {
  const { open, setOpen, setRefetch, itemId, setItemId } = props;
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const [hasError, setHasError] = useState(false);
  const { submit, isSubmitting } = useFormSubmit();

  const formik = useFormik({
    initialValues: {
      name: '',
      unit_id: '',
      category_id: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      if (itemId) {
        let newValues = { ...values };
        if (typeof values.image === 'string') delete newValues.image;
        submit(
          { type: 'postUpdate', contentType: 'formData' },
          newValues,
          '/admin/product',
          values.name,
          itemId,
          false,
          handleFinishRequest
        );
      } else
        submit(
          { type: 'post', contentType: 'formData' },
          values,
          '/admin/product',
          values.name,
          null,
          false,
          handleFinishRequest
        );
    },
  });

  const { isLoading, isFetching } = useQuery({
    queryKey: '/admin/product/edit/{itemId}',
    queryFn: async function () {
      const response = await axiosPrivate.get(`/admin/product/edit/${itemId}`);
      return response.data.data;
    },
    onSuccess: (data) => {
      formik.setValues({
        name: data?.name || '',
        category_id: data?.category_id || '',
        unit_id: data?.unit_id
      });
    },
    enabled: !hasError && !!itemId,
    onError: (error) => {
      setHasError(true);
    },
    retry: false,
  });

  const handleFinishRequest = () => {
    setRefetch(true);
    handleClose();
  };

  const handleClose = () => {
    setItemId('');
    setOpen(false);
    formik.resetForm();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
      maxWidth='md'
      PaperProps={{
        className: '!base-border !shadow-none',
      }}
      disableEscapeKeyDown={true}
    >
      <DialogTitle
        id='alert-dialog-title'
        className='!text-center !bg-gray-100 !mb-2'
      >
        {itemId ? (
          <span>{t('products.editTitle')}</span>
        ) : (
          <span>{t('products.addTitle')}</span>
        )}
      </DialogTitle>

      <DialogContent>
        {isLoading || isFetching ? (
          <div className='circular-progress-box py-5'>
            <CircularProgress size={35} />
          </div>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <Grid
              container
              spacing={{ xs: 2, sm: 3, lg: 3 }}
              rowSpacing={1}
              columns={{ xs: 12, sm: 12, lg: 12 }}
            >
              <Grid item={true} lg={6} sm={6} xs={12}>
                <FormTextField
                  delay={0.1}
                  label={t('common.fields.simpleName')}
                  fieldName='name'
                  formik={formik}
                />
              </Grid>

              <Grid item={true} lg={6} sm={6} xs={12}>
                <FormSelectField
                  delay={0.4}
                  label={t('common.fields.unit')}
                  fieldName='unit_id'
                  formik={formik}
                  path={'/dictionary/units'}
                />
              </Grid>

              <Grid item={true} lg={6} sm={6} xs={12}>
                <FormSelectField
                  delay={0.4}
                  label={t('common.fields.category')}
                  fieldName='category_id'
                  formik={formik}
                  path={'/dictionary/product-category'}
                />
              </Grid>

              <Grid item={true} sm={12} xs={12}>
                <FormActionButtons
                  delay={0.6}
                  isSubmitting={isSubmitting}
                  formType='dialog'
                  setOpen={setOpen}
                  reset={formik.resetForm}
                />
              </Grid>
            </Grid>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StaffAddEditModal;
